// ============================================================================
// UNIFIED REPORT ENGINE — MASTER TYPE DEFINITIONS
// ============================================================================
// Single source of truth for all document types, section types, template
// interfaces, and merge tag types used across the entire report engine.
//
// Phase 1 of the Unified Report Engine Plan
// March 2026
// ============================================================================

// ============================================================================
// DOCUMENT CLASSIFICATION
// ============================================================================

/** High-level category that determines which section types and merge tags are available */
export type DocumentCategory =
  | 'finance_document'   // invoice, quote, credit_note, bill, PO, delivery, receipt, statement
  | 'finance_report'     // P&L, balance sheet, aged debtors/creditors, trial balance, VAT, etc.
  | 'operations_card'    // job_card, attendance_card, service_report, work_order, vehicle_inspection_report
  | 'operations_report'  // project portfolio, service operations overview
  | 'crm_report'         // sales pipeline, conversion, forecast, activity reports
  | 'form_submission'    // compliance forms, inspection forms, certificates
  | 'hr_document'        // payslip, contract, leave report
  | 'time_attendance_report' // employee movement, shift exceptions, daily register
  | 'meeting_page'      // public meeting and booking pages
  | 'user_guide';       // user-docs help pages and section manuals

/** Specific document type — each has its own default sections and merge tags */
export type DocumentType =
  // Finance documents
  | 'price_list'
  | 'invoice'
  | 'quote'
  | 'quote_request'
  | 'billing_submission'
  | 'credit_note'
  | 'debit_note'
  | 'supplier_invoice'
  | 'receipt'
  | 'statement'
  | 'purchase_order'
  | 'delivery_note'
  | 'waybill'
  | 'picking_slip'
  | 'packing_slip'
  | 'stock_adjustment'
  | 'transfer_order'
  | 'sales_order'
  | 'proforma'
  | 'remittance'
  | 'grn'
  | 'proposal'
  | 'supplier_credit'
  | 'supplier_quote'
  | 'supplier_invoice'
  | 'purchase_requisition'
  | 'rfq'
  | 'purchase_return'
  | 'rma'
  | 'purchase'
  | 'invoice_batch'
  // Property finance documents
  | 'levy_cycle'
  | 'meter_reading'
  | 'utility_recovery'
  | 'special_levy'
  | 'scheme_budget'
  | 'reserve_fund'
  | 'deposit_holding'
  | 'trust_transaction'
  | 'payment_dispute'
  | 'payment_plan'
  | 'collection_case'
  | 'invoice_followup'
  | 'credit_request'
  | 'write_off'
  // Expenses
  | 'expense'
  | 'fuel'
  | 'toll'
  | 'maintenance'
  | 'breakdown'
  // Finance reports
  | 'profit_loss'
  | 'balance_sheet'
  | 'trial_balance'
  | 'adjusted_trial_balance'
  | 'cash_flow'
  | 'aged_receivables'
  | 'aged_payables'
  | 'vat_report'
  | 'tax_summary'
  | 'customer_statement'
  | 'debtor_reconciliation'
  | 'creditor_reconciliation'
  | 'general_ledger'
  | 'account_transactions'
  | 'journal_report'
  | 'sales_by_customer'
  | 'sales_by_product'
  | 'purchases_by_supplier'
  | 'expense_by_category'
  | 'invoice_summary'
  | 'bill_summary'
  | 'payment_reminders_sent'
  | 'inventory_valuation'
  | 'stock_movement'
  | 'low_stock_report'
  | 'warehouse_summary'
  | 'product_profitability'
  | 'bank_reconciliation'
  | 'inventory_order_pipeline'
  | 'expense_report'
  | 'cogs_report'
  | 'aging_provision'
  | 'landed_cost_variance'
  | 'stock_count_accuracy'
  | 'gl_reconciliation'
  // Operations
  | 'job_card'
  | 'attendance_card'
  | 'task_card'
  | 'service_report'
  | 'work_order'
  | 'vehicle_inspection_report'
  | 'vehicle_movement_report'
  | 'drawing_transmittal'
  // Digital Voting
  | 'voting_minutes'
  | 'voting_results'
  | 'voting_attendance_register'
  // Forms
  | 'form_submission'
  | 'form_certificate'
  | 'quiz_certificate'
  // HR
  | 'payslip'
  | 'payroll_summary'
  | 'bank_file_report'
  | 'employment_contract'
  | 'leave_report'
  | 'vacancy_advert'
  | 'candidate_cv'
  // Legal
  | 'legal_contract'
  // Workforce Reports
  | 'headcount_analysis'
  | 'turnover_analysis'
  | 'workforce_demographics'
  | 'absenteeism_report'
  | 'new_hires_report'
  | 'tenure_analysis'
  | 'salary_analysis'
  | 'milestones_report'
  | 'payroll_register'
  | 'payroll_costing'
  | 'payroll_deductions'
  | 'ytd_earnings'
  | 'payroll_variance'
  | 'payroll_tax_report'
  | 'ee_workforce_report'
  | 'ee_income_report'
  | 'leave_liability'
  | 'sdl_spend'
  | 'statutory_tracker'
  | 'performance_distribution'
  | 'goals_progress'
  | 'training_compliance'
  | 'training_completion'
  | 'skills_gap'
  | 'contract_expiry_report'
  | 'certification_expiry'
  | 'probation_review'
  | 'disciplinary_cases'
  // Time Attendance Reports
  | 'employee_movement'
  | 'shift_exceptions'
  | 'daily_register'
  | 'late_early'
  | 'shift_violations'
  | 'violation_summary'
  | 'weekly_trend'
  | 'timesheet_summary'
  | 'timesheet_register'
  | 'employee_hours'
  | 'overtime_analysis'
  | 'overtime_requests'
  | 'leave_calendar'
  | 'leave_requests'
  | 'leave_balances'
  | 'employee_roster'
  // Operations reports
  | 'ops_portfolio_summary'
  | 'ops_service_operations_overview'
  | 'ops_project_comprehensive'
  | 'ops_project_financial'
  | 'ops_project_team_performance'
  | 'ops_project_job_breakdown'
  | 'ops_project_performance_scores'
  | 'software_roadmap'
  // CRM reports
  | 'crm_overview'
  | 'crm_pipeline'
  | 'crm_conversion'
  | 'crm_activity'
  | 'crm_forecast'
  | 'crm_performance'
  // Meeting pages
  | 'meeting_book_layout'
  | 'meeting_cancel_page'
  | 'meeting_join_page'
  | 'meeting_room_page'
  | 'meeting_layout'
  | 'meeting_landing_page'
  // User documentation (help centre guides)
  | 'user_guide';

/** Map every document type to its category */
export const DOCUMENT_TYPE_CATEGORY: Record<DocumentType, DocumentCategory> = {
  // Finance documents
  price_list: 'finance_document',
  invoice: 'finance_document',
  quote: 'finance_document',
  quote_request: 'finance_document',
  billing_submission: 'finance_document',
  proposal: 'finance_document',
  credit_note: 'finance_document',
  debit_note: 'finance_document',
  receipt: 'finance_document',
  statement: 'finance_document',
  purchase_order: 'finance_document',
  delivery_note: 'finance_document',
  waybill: 'finance_document',
  picking_slip: 'finance_document',
  packing_slip: 'finance_document',
  stock_adjustment: 'finance_document',
  transfer_order: 'finance_document',
  sales_order: 'finance_document',
  proforma: 'finance_document',
  remittance: 'finance_document',
  grn: 'finance_document',
  supplier_credit: 'finance_document',
  supplier_quote: 'finance_document',
  supplier_invoice: 'finance_document',
  purchase_requisition: 'finance_document',
  rfq: 'finance_document',
  purchase_return: 'finance_document',
  purchase: 'finance_document',
  expense: 'finance_document',
  fuel: 'finance_document',
  toll: 'finance_document',
  maintenance: 'finance_document',
  breakdown: 'finance_document',
  rma: 'finance_document',
  invoice_batch: 'finance_document',
  // Property finance documents
  levy_cycle: 'finance_document',
  meter_reading: 'finance_document',
  utility_recovery: 'finance_document',
  special_levy: 'finance_document',
  scheme_budget: 'finance_document',
  reserve_fund: 'finance_document',
  deposit_holding: 'finance_document',
  trust_transaction: 'finance_document',
  payment_dispute: 'finance_document',
  payment_plan: 'finance_document',
  collection_case: 'finance_document',
  invoice_followup: 'finance_document',
  credit_request: 'finance_document',
  write_off: 'finance_document',
  // Finance reports
  profit_loss: 'finance_report',
  balance_sheet: 'finance_report',
  trial_balance: 'finance_report',
  adjusted_trial_balance: 'finance_report',
  cash_flow: 'finance_report',
  aged_receivables: 'finance_report',
  aged_payables: 'finance_report',
  vat_report: 'finance_report',
  tax_summary: 'finance_report',
  customer_statement: 'finance_report',
  debtor_reconciliation: 'finance_report',
  creditor_reconciliation: 'finance_report',
  general_ledger: 'finance_report',
  account_transactions: 'finance_report',
  journal_report: 'finance_report',
  sales_by_customer: 'finance_report',
  sales_by_product: 'finance_report',
  purchases_by_supplier: 'finance_report',
  expense_by_category: 'finance_report',
  invoice_summary: 'finance_report',
  bill_summary: 'finance_report',
  payment_reminders_sent: 'finance_report',
  inventory_valuation: 'finance_report',
  stock_movement: 'finance_report',
  low_stock_report: 'finance_report',
  warehouse_summary: 'finance_report',
  product_profitability: 'finance_report',
  bank_reconciliation: 'finance_report',
  inventory_order_pipeline: 'finance_report',
  expense_report: 'finance_report',
  cogs_report: 'finance_report',
  aging_provision: 'finance_report',
  landed_cost_variance: 'finance_report',
  stock_count_accuracy: 'finance_report',
  gl_reconciliation: 'finance_report',
  // Operations
  job_card: 'operations_card',
  attendance_card: 'operations_card',
  task_card: 'operations_card',
  service_report: 'operations_card',
  work_order: 'operations_card',
  vehicle_inspection_report: 'operations_card',
  vehicle_movement_report: 'operations_card',
  drawing_transmittal: 'operations_card',
  voting_minutes: 'operations_card',
  voting_results: 'operations_card',
  voting_attendance_register: 'operations_card',
  // Operations reports
  ops_portfolio_summary: 'operations_report',
  ops_service_operations_overview: 'operations_report',
  ops_project_comprehensive: 'operations_report',
  ops_project_financial: 'operations_report',
  ops_project_team_performance: 'operations_report',
  ops_project_job_breakdown: 'operations_report',
  ops_project_performance_scores: 'operations_report',
  software_roadmap: 'operations_report',
  crm_overview: 'crm_report',
  crm_pipeline: 'crm_report',
  crm_conversion: 'crm_report',
  crm_activity: 'crm_report',
  crm_forecast: 'crm_report',
  crm_performance: 'crm_report',
  // Forms
  form_submission: 'form_submission',
  form_certificate: 'form_submission',
  quiz_certificate: 'form_submission',
  // HR
  payslip: 'hr_document',
  payroll_summary: 'hr_document',
  bank_file_report: 'hr_document',
  employment_contract: 'hr_document',
  leave_report: 'hr_document',
  vacancy_advert: 'hr_document',
  candidate_cv: 'hr_document',
  // Legal
  legal_contract: 'hr_document',
  // Workforce Reports
  headcount_analysis: 'hr_document',
  turnover_analysis: 'hr_document',
  workforce_demographics: 'hr_document',
  absenteeism_report: 'hr_document',
  new_hires_report: 'hr_document',
  tenure_analysis: 'hr_document',
  salary_analysis: 'hr_document',
  milestones_report: 'hr_document',
  payroll_register: 'hr_document',
  payroll_costing: 'hr_document',
  payroll_deductions: 'hr_document',
  ytd_earnings: 'hr_document',
  payroll_variance: 'hr_document',
  payroll_tax_report: 'hr_document',
  ee_workforce_report: 'hr_document',
  ee_income_report: 'hr_document',
  leave_liability: 'hr_document',
  sdl_spend: 'hr_document',
  statutory_tracker: 'hr_document',
  performance_distribution: 'hr_document',
  goals_progress: 'hr_document',
  training_compliance: 'hr_document',
  training_completion: 'hr_document',
  skills_gap: 'hr_document',
  contract_expiry_report: 'hr_document',
  certification_expiry: 'hr_document',
  probation_review: 'hr_document',
  disciplinary_cases: 'hr_document',
  // Time Attendance Reports
  employee_movement: 'time_attendance_report',
  shift_exceptions: 'time_attendance_report',
  daily_register: 'time_attendance_report',
  late_early: 'time_attendance_report',
  shift_violations: 'time_attendance_report',
  violation_summary: 'time_attendance_report',
  weekly_trend: 'time_attendance_report',
  timesheet_summary: 'time_attendance_report',
  timesheet_register: 'time_attendance_report',
  employee_hours: 'time_attendance_report',
  overtime_analysis: 'time_attendance_report',
  overtime_requests: 'time_attendance_report',
  leave_calendar: 'time_attendance_report',
  leave_requests: 'time_attendance_report',
  leave_balances: 'time_attendance_report',
  employee_roster: 'time_attendance_report',
  // Meeting pages
  meeting_book_layout: 'meeting_page',
  meeting_cancel_page: 'meeting_page',
  meeting_join_page: 'meeting_page',
  meeting_room_page: 'meeting_page',
  meeting_layout: 'meeting_page',
  meeting_landing_page: 'meeting_page',
  // User documentation
  user_guide: 'user_guide',
};

/** Human-readable labels for each document type */
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  price_list: 'Price List',
  invoice: 'Invoice',
  quote: 'Quote',
  quote_request: 'Quote Request',
  billing_submission: 'Billing Submission',
  proposal: 'Proposal',
  credit_note: 'Credit Note',
  debit_note: 'Debit Note',
  supplier_invoice: 'Supplier Invoice',
  receipt: 'Receipt',
  statement: 'Statement',
  purchase_order: 'Purchase Order',
  delivery_note: 'Delivery Note',
  waybill: 'Waybill',
  picking_slip: 'Picking Slip',
  packing_slip: 'Packing Slip',
  stock_adjustment: 'Stock Adjustment',
  transfer_order: 'Transfer Order',
  sales_order: 'Sales Order',
  proforma: 'Proforma Invoice',
  remittance: 'Remittance Advice',
  grn: 'Goods Received Note',
  supplier_credit: 'Supplier Credit',
  supplier_quote: 'Supplier Quote',
  purchase_requisition: 'Purchase Requisition',
  rfq: 'Request for Quote',
  purchase_return: 'Purchase Return',
  purchase: 'Purchase',
  expense: 'Expense',
  fuel: 'Fuel Expense',
  toll: 'Toll Fee',
  maintenance: 'Maintenance',
  breakdown: 'Breakdown',
  rma: 'Return Merchandise Authorization',
  invoice_batch: 'Invoice Batch',
  levy_cycle: 'Levy Cycle',
  meter_reading: 'Meter Reading',
  utility_recovery: 'Utility Recovery',
  special_levy: 'Special Levy',
  scheme_budget: 'Scheme Budget',
  reserve_fund: 'Reserve Fund Plan',
  deposit_holding: 'Deposit Holding',
  trust_transaction: 'Trust Transaction',
  payment_dispute: 'Payment Dispute',
  payment_plan: 'Payment Plan',
  collection_case: 'Collection Case',
  invoice_followup: 'Invoice Follow-Up',
  credit_request: 'Credit Request',
  write_off: 'Write-Off',
  profit_loss: 'Profit & Loss',
  balance_sheet: 'Balance Sheet',
  trial_balance: 'Trial Balance',
  adjusted_trial_balance: 'Adjusted Trial Balance',
  cash_flow: 'Cash Flow Statement',
  aged_receivables: 'Aged Receivables',
  aged_payables: 'Aged Payables',
  vat_report: 'VAT Report',
  tax_summary: 'Tax Summary',
  customer_statement: 'Customer Statement',
  debtor_reconciliation: 'Debtor Reconciliation',
  creditor_reconciliation: 'Creditor Reconciliation',
  general_ledger: 'General Ledger',
  account_transactions: 'Account Transactions',
  journal_report: 'Journal Report',
  sales_by_customer: 'Sales by Customer',
  sales_by_product: 'Sales by Product',
  purchases_by_supplier: 'Purchases by Supplier',
  expense_by_category: 'Expense by Category',
  invoice_summary: 'Invoice Summary',
  bill_summary: 'Bill Summary',
  payment_reminders_sent: 'Payment Reminders Sent',
  inventory_valuation: 'Inventory Valuation',
  stock_movement: 'Stock Movement Report',
  low_stock_report: 'Low Stock Alert Report',
  warehouse_summary: 'Warehouse Summary',
  product_profitability: 'Product Profitability',
  bank_reconciliation: 'Bank Reconciliation Activity',
  inventory_order_pipeline: 'Inventory Orders & Work Orders',
  expense_report: 'Expense Report',
  cogs_report: 'COGS Report',
  aging_provision: 'Aging & Obsolescence Provision',
  landed_cost_variance: 'Landed Cost Variance',
  stock_count_accuracy: 'Stock Count Accuracy',
  gl_reconciliation: 'GL Reconciliation',
  job_card: 'Job Card',
  attendance_card: 'Attendance Card',
  task_card: 'Task Card',
  service_report: 'Service Report',
  work_order: 'Work Order',
  vehicle_inspection_report: 'Vehicle Inspection Report',
  vehicle_movement_report: 'Vehicle Movement Report',
  drawing_transmittal: 'Drawing Transmittal Register',
  voting_minutes: 'Meeting Minutes & Voting Record',
  voting_results: 'Voting Results Report',
  voting_attendance_register: 'Voting Attendance Register',
  form_submission: 'Form Submission',
  form_certificate: 'Form Certificate',
  quiz_certificate: 'Quiz Certificate',
  payslip: 'Payslip',
  payroll_summary: 'Payroll Summary',
  bank_file_report: 'Bank File Report',
  employment_contract: 'Employment Contract',
  leave_report: 'Leave Report',
  vacancy_advert: 'Vacancy Advert',
  candidate_cv: 'Candidate CV',
  legal_contract: 'Legal Contract',
  headcount_analysis: 'Headcount Analysis',
  turnover_analysis: 'Employee Turnover Analysis',
  workforce_demographics: 'Workforce Demographics',
  absenteeism_report: 'Absenteeism Report',
  new_hires_report: 'New Hires Report',
  tenure_analysis: 'Employee Tenure Analysis',
  salary_analysis: 'Salary & Compensation Analysis',
  milestones_report: 'Birthdays & Anniversaries',
  payroll_register: 'Payroll Register',
  payroll_costing: 'Payroll Costing by Department',
  payroll_deductions: 'Deductions Summary',
  ytd_earnings: 'Year-to-Date Earnings',
  payroll_variance: 'Payroll Variance Report',
  payroll_tax_report: 'Tax Summary Report',
  ee_workforce_report: 'Employment Equity - Workforce Profile (EEA2)',
  ee_income_report: 'Employment Equity - Income Differentials (EEA4)',
  leave_liability: 'Leave Liability Report',
  sdl_spend: 'Skills Development Levy Spend',
  statutory_tracker: 'Statutory Submission Tracker',
  performance_distribution: 'Performance Rating Distribution',
  goals_progress: 'Goals Progress Report',
  training_compliance: 'Training Compliance Report',
  training_completion: 'Training Completion Report',
  skills_gap: 'Skills Gap Analysis',
  contract_expiry_report: 'Contract Expiry Report',
  certification_expiry: 'Certification Expiry Report',
  probation_review: 'Probation Review Tracker',
  disciplinary_cases: 'Disciplinary Cases Report',
  employee_movement: 'Employee Movement Log',
  shift_exceptions: 'Shift Exception Report',
  daily_register: 'Daily Attendance Register',
  late_early: 'Late Comers & Early Leavers',
  shift_violations: 'Shift Violations & Geofence Compliance',
  violation_summary: 'Violation Summary by Employee',
  weekly_trend: 'Weekly Attendance Trend',
  timesheet_summary: 'Timesheet Summary',
  timesheet_register: 'Timesheet Register',
  employee_hours: 'Employee Hours Summary',
  overtime_analysis: 'Overtime Analysis',
  overtime_requests: 'Overtime Request Log',
  leave_calendar: 'Leave Calendar',
  leave_requests: 'Leave Request Log',
  leave_balances: 'Leave Balance Summary',
  employee_roster: 'Employee Attendance Roster',
  ops_portfolio_summary: 'Project Portfolio Summary',
  ops_service_operations_overview: 'Service Operations Overview',
  ops_project_comprehensive: 'Comprehensive Project Report',
  ops_project_financial: 'Project Financial Summary',
  ops_project_team_performance: 'Project Team Performance',
  ops_project_job_breakdown: 'Project Job Breakdown',
  ops_project_performance_scores: 'Project Performance Scores',
  software_roadmap: 'Software Development Roadmap',
  crm_overview: 'Sales Overview Report',
  crm_pipeline: 'Sales Pipeline Report',
  crm_conversion: 'Conversion Funnel Report',
  crm_activity: 'Sales Activity Report',
  crm_forecast: 'Sales Forecast Report',
  crm_performance: 'Team Performance Report',
  meeting_book_layout: 'Meeting Booking Layout',
  meeting_cancel_page: 'Meeting Cancellation Page',
  meeting_join_page: 'Guest Join Page',
  meeting_room_page: 'Meeting Room Redirect',
  meeting_layout: 'Meeting Shell Layout',
  meeting_landing_page: 'Meeting Landing Page',
  user_guide: 'User Guide',
};

const DEFAULT_TEMPLATE_NAME_OVERRIDES: Partial<Record<DocumentType, string>> = {
  vehicle_inspection_report: 'Inspection layout',
};

export function getDefaultTemplateName(documentType: DocumentType, formTitle?: string): string {
  if (formTitle) {
    return `${formTitle} Template`;
  }

  return DEFAULT_TEMPLATE_NAME_OVERRIDES[documentType]
    || `${DOCUMENT_TYPE_LABELS[documentType]} Template`;
}

/** Icons (emoji) for each document type */
export const DOCUMENT_TYPE_ICONS: Record<DocumentType, string> = {
  price_list: '🏷️',
  invoice: '📄',
  quote: '💼',
  quote_request: '📝',
  billing_submission: '📋',
  proposal: '📋',
  credit_note: '💳',
  debit_note: '🧾',
  supplier_invoice: '🧾',
  receipt: '🧮',
  statement: '📊',
  purchase_order: '📋',
  delivery_note: '🚚',
  waybill: '🚛',
  picking_slip: '🧾',
  packing_slip: '📦',
  stock_adjustment: '⚖️',
  transfer_order: '🔄',
  sales_order: '🛒',
  proforma: '📝',
  remittance: '💰',
  grn: '📦',
  supplier_credit: '↩️',
  supplier_quote: '📨',
  purchase_requisition: '🗂️',
  rfq: '📨',
  purchase_return: '↩️',
  rma: '🔁',
  purchase: '🛒',
  invoice_batch: '📦',
  // Property finance
  levy_cycle: '🏢',
  meter_reading: '⚡',
  utility_recovery: '💧',
  special_levy: '🏗️',
  scheme_budget: '📋',
  reserve_fund: '🏦',
  deposit_holding: '🔒',
  trust_transaction: '🤝',
  payment_dispute: '⚠️',
  payment_plan: '📅',
  collection_case: '📂',
  invoice_followup: '🔔',
  credit_request: '💳',
  write_off: '✖️',
  // Expenses
  expense: '💳',
  fuel: '⛽',
  toll: '🛣️',
  maintenance: '🔧',
  breakdown: '⚠️',
  profit_loss: '📈',
  balance_sheet: '⚖️',
  trial_balance: '📐',
  adjusted_trial_balance: '📐',
  cash_flow: '💧',
  aged_receivables: '📅',
  aged_payables: '📅',
  vat_report: '🏛️',
  tax_summary: '🏛️',
  customer_statement: '👤',
  debtor_reconciliation: '🔍',
  creditor_reconciliation: '🔍',
  general_ledger: '📒',
  account_transactions: '📒',
  journal_report: '📒',
  sales_by_customer: '📊',
  sales_by_product: '📊',
  purchases_by_supplier: '📊',
  expense_by_category: '📊',
  invoice_summary: '📊',
  bill_summary: '📊',
  payment_reminders_sent: '🔔',
  inventory_valuation: '📦',
  stock_movement: '📈',
  low_stock_report: '⚠️',
  warehouse_summary: '🏬',
  product_profitability: '💹',
  bank_reconciliation: '🏦',
  inventory_order_pipeline: '🔗',
  expense_report: '💳',
  cogs_report: '💸',
  aging_provision: '📅',
  landed_cost_variance: '🚢',
  stock_count_accuracy: '✓',
  gl_reconciliation: '🔍',
  job_card: '🔧',
  attendance_card: '📋',
  task_card: '✅',
  service_report: '🔧',
  work_order: '⚙️',
  vehicle_inspection_report: '🚘',
  vehicle_movement_report: '🗺️',
  drawing_transmittal: '📐',
  voting_minutes: '🗳️',
  voting_results: '📊',
  voting_attendance_register: '📋',
  form_submission: '📝',
  form_certificate: '🏆',
  quiz_certificate: '🎓',
  payslip: '💵',
  payroll_summary: '📊',
  bank_file_report: '🏦',
  employment_contract: '📃',
  leave_report: '🏖️',
  vacancy_advert: '📢',
  candidate_cv: '👤',
  legal_contract: '📋',
  headcount_analysis: '👥',
  turnover_analysis: '🔄',
  workforce_demographics: '📊',
  absenteeism_report: '🏠',
  new_hires_report: '🆕',
  tenure_analysis: '📅',
  salary_analysis: '💰',
  milestones_report: '🎂',
  payroll_register: '📋',
  payroll_costing: '💵',
  payroll_deductions: '📉',
  ytd_earnings: '📈',
  payroll_variance: '📊',
  payroll_tax_report: '🧾',
  ee_workforce_report: '⚖️',
  ee_income_report: '⚖️',
  leave_liability: '🏖️',
  sdl_spend: '🎓',
  statutory_tracker: '📑',
  performance_distribution: '⭐',
  goals_progress: '🎯',
  training_compliance: '✅',
  training_completion: '🎓',
  skills_gap: '🧩',
  contract_expiry_report: '📃',
  certification_expiry: '📜',
  probation_review: '🔍',
  disciplinary_cases: '⚠️',
  employee_movement: '🚶',
  shift_exceptions: '⚠️',
  daily_register: '📋',
  late_early: '🕐',
  shift_violations: '🚨',
  violation_summary: '📊',
  weekly_trend: '📈',
  timesheet_summary: '🕒',
  timesheet_register: '📋',
  employee_hours: '⏱️',
  overtime_analysis: '📊',
  overtime_requests: '📝',
  leave_calendar: '📅',
  leave_requests: '🏖️',
  leave_balances: '⚖️',
  employee_roster: '👥',
  ops_portfolio_summary: '🗂️',
  ops_service_operations_overview: '🔧',
  ops_project_comprehensive: '📋',
  ops_project_financial: '💰',
  ops_project_team_performance: '👷',
  ops_project_job_breakdown: '🧱',
  ops_project_performance_scores: '🏆',
  software_roadmap: '🗺️',
  crm_overview: '📈',
  crm_pipeline: '🔄',
  crm_conversion: '🎯',
  crm_activity: '📞',
  crm_forecast: '🔮',
  crm_performance: '🏅',
  meeting_book_layout: '📅',
  meeting_cancel_page: '❌',
  meeting_join_page: '🚪',
  meeting_room_page: '🎥',
  meeting_layout: '🖥️',
  meeting_landing_page: '📹',
  user_guide: '📖',
};

/** Category labels and colors for the UI */
export const DOCUMENT_CATEGORY_META: Record<DocumentCategory, { label: string; color: string; icon: string }> = {
  finance_document: { label: 'Finance Documents', color: '#2563eb', icon: '📄' },
  finance_report: { label: 'Finance Reports', color: '#7c3aed', icon: '📊' },
  operations_card: { label: 'Operations Cards', color: '#059669', icon: '🔧' },
  operations_report: { label: 'Operations Reports', color: '#334155', icon: '🗂️' },
  crm_report: { label: 'CRM Reports', color: '#e11d48', icon: '💼' },
  form_submission: { label: 'Forms & Certificates', color: '#d97706', icon: '📝' },
  hr_document: { label: 'HR Documents', color: '#dc2626', icon: '👤' },
  time_attendance_report: { label: 'Time Attendance Reports', color: '#0891b2', icon: '⏰' },
  meeting_page: { label: 'Meeting Pages', color: '#14b8a6', icon: '📹' },
  user_guide: { label: 'User Guides', color: '#0e7490', icon: '📖' },
};

/**
 * Client-safe list of document types currently supported by the unified
 * generator routes. Keep this in sync with the server adapter registry.
 */
export const SUPPORTED_UNIFIED_REPORT_DOCUMENT_TYPES: DocumentType[] = [
  // Finance documents
  'price_list',
  'invoice',
  'quote',
  'billing_submission',
  'proposal',
  'credit_note',
  'debit_note',
  'supplier_invoice',
  'receipt',
  'statement',
  'purchase_order',
  'delivery_note',
  'waybill',
  'picking_slip',
  'packing_slip',
  'stock_adjustment',
  'transfer_order',
  'sales_order',
  'proforma',
  'remittance',
  'grn',
  'supplier_credit',
  'supplier_quote',
  'supplier_invoice',
  'purchase_requisition',
  'rfq',
  'purchase_return',
  'rma',
  'purchase',
  'invoice_batch',
  // Property finance documents
  'levy_cycle',
  'meter_reading',
  'utility_recovery',
  'special_levy',
  'scheme_budget',
  'reserve_fund',
  'deposit_holding',
  'trust_transaction',
  'payment_dispute',
  'payment_plan',
  'collection_case',
  'invoice_followup',
  'credit_request',
  'write_off',
  // Expenses
  'expense',
  'fuel',
  'toll',
  'maintenance',
  'breakdown',
  // Finance reports
  'profit_loss',
  'balance_sheet',
  'trial_balance',
  'adjusted_trial_balance',
  'cash_flow',
  'aged_receivables',
  'aged_payables',
  'vat_report',
  'tax_summary',
  'customer_statement',
  'debtor_reconciliation',
  'creditor_reconciliation',
  'general_ledger',
  'account_transactions',
  'journal_report',
  'sales_by_customer',
  'sales_by_product',
  'purchases_by_supplier',
  'expense_by_category',
  'invoice_summary',
  'bill_summary',
  'payment_reminders_sent',
  'inventory_valuation',
  'stock_movement',
  'low_stock_report',
  'warehouse_summary',
  'product_profitability',
  'bank_reconciliation',
  'inventory_order_pipeline',
  'expense_report',
  'cogs_report',
  'aging_provision',
  'landed_cost_variance',
  'stock_count_accuracy',
  'gl_reconciliation',
  // Operations
  'job_card',
  'attendance_card',
  'task_card',
  'service_report',
  'work_order',
  'vehicle_inspection_report',
  'vehicle_movement_report',
  'drawing_transmittal',
  // Digital Voting
  'voting_minutes',
  'voting_results',
  'voting_attendance_register',
  // Forms
  'form_submission',
  'form_certificate',
  'quiz_certificate',
  // HR
  'payslip',
  'payroll_summary',
  'bank_file_report',
  'employment_contract',
  'leave_report',
  'vacancy_advert',
  'candidate_cv',
  // Legal
  'legal_contract',
  // Workforce Reports
  'headcount_analysis',
  'turnover_analysis',
  'workforce_demographics',
  'absenteeism_report',
  'new_hires_report',
  'tenure_analysis',
  'salary_analysis',
  'milestones_report',
  'payroll_register',
  'payroll_costing',
  'payroll_deductions',
  'ytd_earnings',
  'payroll_variance',
  'payroll_tax_report',
  'ee_workforce_report',
  'ee_income_report',
  'leave_liability',
  'sdl_spend',
  'statutory_tracker',
  'performance_distribution',
  'goals_progress',
  'training_compliance',
  'training_completion',
  'skills_gap',
  'contract_expiry_report',
  'certification_expiry',
  'probation_review',
  'disciplinary_cases',
  // Time Attendance Reports
  'employee_movement',
  'shift_exceptions',
  'daily_register',
  'late_early',
  'shift_violations',
  'violation_summary',
  'weekly_trend',
  'timesheet_summary',
  'timesheet_register',
  'employee_hours',
  'overtime_analysis',
  'overtime_requests',
  'leave_calendar',
  'leave_requests',
  'leave_balances',
  'employee_roster',
  // Operations reports
  'ops_portfolio_summary',
  'ops_service_operations_overview',
  'ops_project_comprehensive',
  'ops_project_financial',
  'ops_project_team_performance',
  'ops_project_job_breakdown',
  'ops_project_performance_scores',
  'software_roadmap',
  // CRM reports
  'crm_overview',
  'crm_pipeline',
  'crm_conversion',
  'crm_activity',
  'crm_forecast',
  'crm_performance',
  // Meeting pages
  'meeting_book_layout',
  'meeting_cancel_page',
  'meeting_join_page',
  'meeting_room_page',
  'meeting_layout',
  'meeting_landing_page',
  // User documentation
  'user_guide',
];

export function isUnifiedReportDocumentType(value: string): value is DocumentType {
  return (SUPPORTED_UNIFIED_REPORT_DOCUMENT_TYPES as string[]).includes(value);
}

/**
 * Explicit mapping from report library definition IDs (`time_*` reports in
 * reportDefinitions.ts) to their unified DocumentType for PDF generation.
 * Kept in sync with TIME_ATTENDANCE_REPORTS — every entry must resolve to a
 * type registered in SUPPORTED_UNIFIED_REPORT_DOCUMENT_TYPES.
 */
export const TIME_ATTENDANCE_REPORT_DOCUMENT_TYPES: Record<string, DocumentType> = {
  time_employee_movement: 'employee_movement',
  time_shift_exceptions: 'shift_exceptions',
  time_daily_register: 'daily_register',
  time_late_early: 'late_early',
  time_shift_violations: 'shift_violations',
  time_weekly_trend: 'weekly_trend',
  time_timesheet_summary: 'timesheet_summary',
  time_timesheet_register: 'timesheet_register',
  time_employee_hours: 'employee_hours',
  time_overtime_analysis: 'overtime_analysis',
  time_overtime_requests: 'overtime_requests',
  time_leave_calendar: 'leave_calendar',
  time_leave_requests: 'leave_requests',
  time_leave_balances: 'leave_balances',
  time_employee_roster: 'employee_roster',
  time_violation_summary: 'violation_summary',
};

/**
 * Explicit mapping from report library definition IDs (workforce reports in
 * reportDefinitions.ts) to their unified DocumentType for PDF generation.
 * Kept in sync with HR_ANALYTICS/PAYROLL/COMPLIANCE/PERFORMANCE/TRAINING/
 * OPERATIONAL reports — every entry must resolve to a type registered in
 * SUPPORTED_UNIFIED_REPORT_DOCUMENT_TYPES.
 */
export const WORKFORCE_REPORT_DOCUMENT_TYPES: Record<string, DocumentType> = {
  hr_headcount_analysis: 'headcount_analysis',
  hr_turnover_analysis: 'turnover_analysis',
  hr_demographics: 'workforce_demographics',
  hr_absenteeism: 'absenteeism_report',
  hr_new_hires: 'new_hires_report',
  hr_tenure_analysis: 'tenure_analysis',
  hr_salary_analysis: 'salary_analysis',
  hr_birthdays_anniversaries: 'milestones_report',
  payroll_register: 'payroll_register',
  payroll_costing: 'payroll_costing',
  payroll_deductions: 'payroll_deductions',
  payroll_ytd_earnings: 'ytd_earnings',
  payroll_variance: 'payroll_variance',
  payroll_tax_summary: 'payroll_tax_report',
  compliance_ee_workforce: 'ee_workforce_report',
  compliance_ee_income: 'ee_income_report',
  compliance_leave_liability: 'leave_liability',
  compliance_sdl_spend: 'sdl_spend',
  compliance_statutory_submissions: 'statutory_tracker',
  performance_distribution: 'performance_distribution',
  performance_goals_progress: 'goals_progress',
  training_compliance: 'training_compliance',
  training_completion: 'training_completion',
  training_skills_gap: 'skills_gap',
  ops_contract_expiry: 'contract_expiry_report',
  ops_certification_expiry: 'certification_expiry',
  ops_probation_review: 'probation_review',
  ops_disciplinary_cases: 'disciplinary_cases',
};

/**
 * Operations report definition IDs (operationsReportDefinitions.ts) that can
 * run server-side without a project context → unified DocumentType.
 */
export const OPERATIONS_REPORT_DOCUMENT_TYPES: Record<string, DocumentType> = {
  ops_portfolio_summary: 'ops_portfolio_summary',
  ops_service_operations_overview: 'ops_service_operations_overview',
  ops_project_comprehensive: 'ops_project_comprehensive',
  ops_project_financial: 'ops_project_financial',
  ops_project_team_performance: 'ops_project_team_performance',
  ops_project_job_breakdown: 'ops_project_job_breakdown',
  ops_project_performance_scores: 'ops_project_performance_scores',
  software_roadmap: 'software_roadmap',
};

/** Operations report definition IDs that require a project_id parameter */
export const PROJECT_SCOPED_OPS_REPORTS = new Set([
  'ops_project_comprehensive',
  'ops_project_financial',
  'ops_project_team_performance',
  'ops_project_job_breakdown',
  'ops_project_performance_scores',
]);

/** Sub-endpoint used by each project-scoped operations report */
export const PROJECT_OPS_REPORT_ENDPOINTS: Record<string, string> = {
  ops_project_financial: 'financial-summary',
  ops_project_team_performance: 'team-performance',
  ops_project_job_breakdown: 'job-breakdown',
  ops_project_performance_scores: 'performance-scores',
};

/** CRM report definition ID → document type */
export const CRM_REPORT_DOCUMENT_TYPES: Record<string, DocumentType> = {
  crm_overview: 'crm_overview',
  crm_pipeline: 'crm_pipeline',
  crm_conversion: 'crm_conversion',
  crm_activity: 'crm_activity',
  crm_forecast: 'crm_forecast',
  crm_performance: 'crm_performance',
};

/** CRM report definition ID → /api/v1/crm/reports `type` query value */
export const CRM_REPORT_API_TYPES: Record<string, string> = {
  crm_overview: 'overview',
  crm_pipeline: 'pipeline',
  crm_conversion: 'conversion',
  crm_activity: 'activity',
  crm_forecast: 'forecast',
  crm_performance: 'performance',
};

/** Combined lookup: any report library definition ID → unified DocumentType */
export const REPORT_LIBRARY_DOCUMENT_TYPES: Record<string, DocumentType> = {
  ...WORKFORCE_REPORT_DOCUMENT_TYPES,
  ...TIME_ATTENDANCE_REPORT_DOCUMENT_TYPES,
  ...OPERATIONS_REPORT_DOCUMENT_TYPES,
  ...CRM_REPORT_DOCUMENT_TYPES,
};

/** Group document types by category for UI dropdowns */
export function getDocumentTypesByCategory(): Array<{
  category: DocumentCategory;
  label: string;
  color: string;
  types: Array<{ value: DocumentType; label: string; icon: string }>;
}> {
  const groups: Record<DocumentCategory, Array<{ value: DocumentType; label: string; icon: string }>> = {
    finance_document: [],
    finance_report: [],
    operations_card: [],
    operations_report: [],
    form_submission: [],
    hr_document: [],
    time_attendance_report: [],
    crm_report: [],
    meeting_page: [],
    user_guide: [],
  };

  for (const [type, category] of Object.entries(DOCUMENT_TYPE_CATEGORY)) {
    groups[category].push({
      value: type as DocumentType,
      label: DOCUMENT_TYPE_LABELS[type as DocumentType],
      icon: DOCUMENT_TYPE_ICONS[type as DocumentType],
    });
  }

  return Object.entries(groups)
    .filter(([, types]) => types.length > 0)
    .map(([category, types]) => ({
      category: category as DocumentCategory,
      label: DOCUMENT_CATEGORY_META[category as DocumentCategory].label,
      color: DOCUMENT_CATEGORY_META[category as DocumentCategory].color,
      types,
    }));
}

// ============================================================================
// SECTION TYPES
// ============================================================================

/** Every possible section type available across all document categories */
export type UnifiedSectionType =
  // === Universal sections (all categories) ===
  | 'header'              // Company logo + banner + document title
  | 'custom_text'         // Free-form rich text / merge tags
  | 'spacer'              // Vertical space
  | 'divider'             // Horizontal line
  | 'footer'              // Page footer with branding + optional disclaimer text
  // === Form & operations sections ===
  | 'meta_info'           // Summary cards (submission date, status, etc.)
  | 'fields_table'        // Form field key-value pairs
  | 'section_fields'      // Grouped fields by section
  | 'signature_block'     // Signature pads/images
  | 'certificate_box'     // Certificate number, validity, QR
  // === Finance document sections ===
  | 'company_info'        // From/To company details
  | 'document_details'    // Doc number, dates, reference, PO#
  | 'line_items_table'    // Invoice/quote line items with configurable columns
  | 'totals_block'        // Subtotal, discount, tax, grand total
  | 'bank_details'        // Bank payment details
  | 'terms_notes'         // Terms & conditions, notes, payment terms
  | 'payment_info'        // Amount paid, balance due, payment history
  // === Report sections ===
  | 'data_table'          // Generic data table (P&L rows, aged debtors, GL entries)
  | 'summary_row'         // Highlight total row
  | 'two_column'          // Side-by-side layout (debtor vs creditor)
  | 'kpi_cards'           // KPI summary metric cards
  // === Operations-specific sections ===
  | 'contact_info'        // Customer/site contact details
  | 'checklist'           // Task checklist with pass/fail
  | 'time_log'            // Time entries
  | 'materials_bom'       // Bill of materials / parts used
  | 'photo_grid'          // Photo evidence grid
  | 'gps_location';       // GPS coordinates / map reference

/**
 * Metadata for each section type — label, icon (Lucide name), description,
 * which document categories it applies to.
 */
export interface SectionTypeMeta {
  type: UnifiedSectionType;
  label: string;
  icon: string;           // Lucide icon name
  description: string;
  categories: DocumentCategory[];
}

export const SECTION_TYPE_META: SectionTypeMeta[] = [
  // Universal
  { type: 'header', label: 'Header Banner', icon: 'LayoutTemplate', description: 'Company logo, document title, subtitle', categories: ['finance_document', 'finance_report', 'operations_card', 'form_submission', 'hr_document', 'meeting_page', 'user_guide'] },
  { type: 'custom_text', label: 'Custom Text', icon: 'Type', description: 'Free-form text with merge tags', categories: ['finance_document', 'finance_report', 'operations_card', 'form_submission', 'hr_document', 'meeting_page', 'user_guide'] },
  { type: 'spacer', label: 'Spacer', icon: 'ArrowDown', description: 'Vertical whitespace between sections', categories: ['finance_document', 'finance_report', 'operations_card', 'form_submission', 'hr_document', 'meeting_page', 'user_guide'] },
  { type: 'divider', label: 'Divider Line', icon: 'Minus', description: 'Horizontal separator line', categories: ['finance_document', 'finance_report', 'operations_card', 'form_submission', 'hr_document', 'meeting_page', 'user_guide'] },
  { type: 'footer', label: 'Page Footer', icon: 'FileText', description: 'Page numbers, generated date, company name, and optional disclaimer text', categories: ['finance_document', 'finance_report', 'operations_card', 'form_submission', 'hr_document', 'meeting_page', 'user_guide'] },
  // Form & operations
  { type: 'meta_info', label: 'Info / Summary Cards', icon: 'Info', description: 'Key-value metadata or summary metric cards', categories: ['operations_card', 'form_submission', 'meeting_page', 'hr_document'] },
  { type: 'fields_table', label: 'Fields Table', icon: 'Table', description: 'Render linked form data or extra sections as rows', categories: ['form_submission', 'operations_card'] },
  { type: 'section_fields', label: 'Content Block', icon: 'Table', description: 'Choose a work-item content block such as comments, timeline, or expenses; on finance PDFs, groups report blocks such as credit terms and PayFast notices; on a candidate CV, renders parsed CV sections in prose layout', categories: ['form_submission', 'operations_card', 'finance_document', 'finance_report', 'hr_document'] },
  { type: 'signature_block', label: 'Signatures', icon: 'FileSignature', description: 'Captured signature images + names', categories: ['operations_card', 'form_submission', 'finance_document'] },
  { type: 'certificate_box', label: 'Certificate Box', icon: 'Award', description: 'Certificate number + validity highlight', categories: ['form_submission', 'operations_card'] },
  // Finance document
  { type: 'company_info', label: 'Company & Contact', icon: 'Building', description: 'From/To: company and customer details', categories: ['finance_document', 'hr_document', 'operations_card'] },
  { type: 'document_details', label: 'Document Details', icon: 'FileText', description: 'Document number, dates, reference, PO#', categories: ['finance_document'] },
  { type: 'line_items_table', label: 'Line Items Table', icon: 'Table', description: 'Invoice/quote line items with columns', categories: ['finance_document'] },
  { type: 'totals_block', label: 'Totals Block', icon: 'Calculator', description: 'Subtotal, discount, tax, grand total', categories: ['finance_document'] },
  { type: 'bank_details', label: 'Bank Details', icon: 'Landmark', description: 'Bank name, account, branch code', categories: ['finance_document'] },
  { type: 'terms_notes', label: 'Terms & Notes', icon: 'FileText', description: 'Terms, conditions, notes, payment terms', categories: ['finance_document', 'operations_card'] },
  { type: 'payment_info', label: 'Payment Info', icon: 'CreditCard', description: 'Amount paid, balance due, payment history', categories: ['finance_document'] },
  // Report
  { type: 'data_table', label: 'Data Table', icon: 'Table', description: 'Generic table for report data', categories: ['finance_report', 'hr_document', 'operations_card', 'user_guide'] },
  { type: 'summary_row', label: 'Summary / Total Row', icon: 'ArrowRight', description: 'Highlighted total or summary row', categories: ['finance_report'] },
  { type: 'two_column', label: 'Two-Column Layout', icon: 'Columns', description: 'Side-by-side data sections', categories: ['finance_report'] },
  { type: 'kpi_cards', label: 'KPI Cards', icon: 'TrendingUp', description: 'Key performance indicator metrics', categories: ['finance_report'] },
  // Operations
  { type: 'contact_info', label: 'Contact / Site Info', icon: 'MapPin', description: 'Customer/site contact and address', categories: ['operations_card'] },
  { type: 'checklist', label: 'Checklist', icon: 'CheckSquare', description: 'Task checklist with pass/fail status', categories: ['operations_card', 'user_guide'] },
  { type: 'time_log', label: 'Time Log', icon: 'Clock', description: 'Time entries and durations', categories: ['operations_card'] },
  { type: 'materials_bom', label: 'Materials / BOM', icon: 'Package', description: 'Bill of materials / parts used', categories: ['operations_card'] },
  { type: 'photo_grid', label: 'Photo Grid', icon: 'Image', description: 'Photo evidence in grid layout', categories: ['operations_card', 'form_submission'] },
  { type: 'gps_location', label: 'GPS Location', icon: 'MapPin', description: 'GPS coordinates and address', categories: ['operations_card', 'form_submission'] },
];

/**
 * Get section types available for a specific document category
 */
export function getSectionTypesForCategory(category: DocumentCategory): SectionTypeMeta[] {
  return SECTION_TYPE_META.filter(s => s.categories.includes(category));
}

/**
 * Get section types available for a specific document type
 */
export function getSectionTypesForDocumentType(docType: DocumentType): SectionTypeMeta[] {
  const category = DOCUMENT_TYPE_CATEGORY[docType];
  return getSectionTypesForCategory(category);
}

// ============================================================================
// TEMPLATE INTERFACES
// ============================================================================

export interface UnifiedSectionConfigKPIItem {
  label?: string;
  valueTag?: string;
}

export interface UnifiedSectionConfigTwoColumnPane {
  label?: string;
  columns?: TemplateTableColumn[];
}

/** Canonical CV section keys that land in the sidebar by default when a
 * candidate_cv template uses a sidebar cvLayout (overridable via the
 * section's sidebarKeys config). */
export const CV_SIDEBAR_DEFAULT_KEYS = [
  'personal_details',
  'skills',
  'languages',
  'education',
  'interests',
  'references',
] as const;

export interface UnifiedSectionConfig {
  [key: string]: unknown;
  align?: TemplateTableColumn['align'];
  alternateRowShading?: boolean;
  bold?: boolean;
  captionFontSize?: number;
  closingDetailsPlacement?: string;
  columns?: number | TemplateTableColumn[];
  content?: string;
  excludeEmpty?: boolean;
  excludeFieldKeys?: string[];
  excludeSectionKeys?: string[];
  fields?: string[];
  fontSize?: number;
  gpaDisplayMode?: string;
  groupBySection?: boolean;
  groupCommentsByAppointment?: boolean;
  height?: number;
  highlightColor?: string;
  includeFieldKeys?: string[];
  includeSectionKeys?: string[];
  items?: UnifiedSectionConfigKPIItem[];
  label?: string;
  layout?: string;
  leftSection?: UnifiedSectionConfigTwoColumnPane;
  /** CV render mode only — list-item spacing: compact | normal | relaxed | spacious. */
  lineSpacing?: string;
  /** CV render mode only — page layout: classic | sidebar-left | sidebar-right.
   * Sidebar layouts move the candidate photo, contact cards and the
   * sidebarKeys sections into a narrow side column (resume-style). */
  cvLayout?: string;
  /** CV sidebar layouts — canonical section keys rendered in the sidebar.
   * Defaults to CV_SIDEBAR_DEFAULT_KEYS. */
  sidebarKeys?: string[];
  /** CV sidebar layouts — 'tinted' (light primary tint) | 'solid' (primary bg, white text). */
  sidebarTheme?: string;
  /** CV render mode only — canonical section keys in desired render order.
   * Listed sections render first in the given order; unlisted keep source
   * order afterwards. */
  cvSectionOrder?: string[];
  /** CV render mode only — main-column section heading style:
   * banner (default filled bar) | underline | plain. */
  cvHeadingStyle?: string;
  /** CV render mode only — render skills-keyed section lines as pill chips:
   * list (default) | chips. */
  cvSkillsStyle?: string;
  /** CV render mode only — candidate photo placement in the summary-card row:
   * left (default) | right. Sidebar layouts always put the photo in the sidebar. */
  photoPosition?: string;
  maxPhotos?: number;
  photoTimestampPrecision?: string;
  renderMode?: string;
  rightSection?: UnifiedSectionConfigTwoColumnPane;
  sectionKey?: string;
  showAccountNumber?: boolean;
  showAccountType?: boolean;
  showAddress?: boolean;
  showAllFields?: boolean;
  showAmountDue?: boolean;
  showAmountPaid?: boolean;
  showAssignedTo?: boolean;
  showBadge?: boolean;
  showBalance?: boolean;
  showBankName?: boolean;
  showBranchCode?: boolean;
  showCaptions?: boolean;
  showCapturedByName?: boolean;
  showCertNumber?: boolean;
  showCommentAuthor?: boolean;
  showCommentDate?: boolean;
  showCommentTime?: boolean;
  showCompanyLogo?: boolean;
  showCompanyName?: boolean;
  showContactDetails?: boolean;
  showCoordinates?: boolean;
  showCost?: boolean;
  showDate?: boolean;
  showDescription?: boolean;
  showDiscount?: boolean;
  showDocumentStatus?: boolean;
  showDuration?: boolean;
  showEmail?: boolean;
  showEmployee?: boolean;
  showEmptyFields?: boolean;
  showExactTimes?: boolean;
  showExpiryDate?: boolean;
  showFormTitle?: boolean;
  showGPS?: boolean;
  showGpsCoordinates?: boolean;
  showGrandTotal?: boolean;
  showIban?: boolean;
  showIssuedDate?: boolean;
  showLifecycleMilestones?: boolean;
  showLogo?: boolean;
  showMap?: boolean;
  showNotes?: boolean;
  showPaymentHistory?: boolean;
  showPaymentTerms?: boolean;
  showPhone?: boolean;
  showPhotoCaption?: boolean;
  showPhotoTimestamp?: boolean;
  showQRCode?: boolean;
  showQuantity?: boolean;
  showReference?: boolean;
  showRowNumbers?: boolean;
  showSignatureImage?: boolean;
  showSignerDesignation?: boolean;
  showSignerName?: boolean;
  showSourceAppointment?: boolean;
  showStartEnd?: boolean;
  showStatus?: boolean;
  showSubtotal?: boolean;
  showSwift?: boolean;
  showTax?: boolean;
  showTerms?: boolean;
  showTimestamp?: boolean;
  showTotal?: boolean;
  showTotalHours?: boolean;
  showTotalRow?: boolean;
  showTravelDetails?: boolean;
  showTravelEntries?: boolean;
  showUploaderInitials?: boolean;
  showWorkDetails?: boolean;
  statusOptions?: string[];
  text?: string;
  value?: string;
  zebraColor?: string;
}

/** A section instance within a template */
export interface UnifiedTemplateSection {
  id: string;
  type: UnifiedSectionType;
  label: string;
  enabled: boolean;
  config: UnifiedSectionConfig;
}

export type LinkedAppointmentsDisplayMode = 'full' | 'summary' | 'hidden';
export type LinkedAppointmentsTimeDisplayMode = 'hidden' | 'duration' | 'arrival_departure' | 'arrival_departure_and_duration';
export type LinkedAppointmentsReferenceLabel = 'attendance_card' | 'appointment_card';

/** Style settings for a template */
export interface UnifiedTemplateStyles {
  primaryColor: string;
  accentColor: string;
  textColor: string;
  backgroundColor: string;
  headerBgColor: string;
  headerTextColor: string;
  fontFamily: 'helvetica' | 'times' | 'courier';
  fontSize: number;
  lineHeight: number;
  margins: { top: number; right: number; bottom: number; left: number };
  headerHeight: number;
  showPageNumbers: boolean;
  showGeneratedDate: boolean;
  /** When false, linked appointment summary cards hide status badges (job cards). Default: show. */
  linkedAppointmentsShowStatus?: boolean;
  /** Controls how linked appointments are displayed in job cards: 'full' = full card, 'summary' = list view with permalink, 'hidden' = don't show. Default: 'full'. */
  linkedAppointmentsDisplayMode?: LinkedAppointmentsDisplayMode;
  /** Controls whether linked appointments show duration only, arrival/departure, both, or no attendance timing. Default: 'duration'. */
  linkedAppointmentsTimeDisplayMode?: LinkedAppointmentsTimeDisplayMode;
  /** Controls whether linked reports are referenced as attendance cards or appointment cards. Default: 'attendance_card'. */
  linkedAppointmentsReferenceLabel?: LinkedAppointmentsReferenceLabel;
  /** When true, show linked appointment GPS proof coordinates in list / summary views. */
  linkedAppointmentsShowGpsProof?: boolean;
  /** When true, show linked appointment fleet vehicle registration in list / summary views. */
  linkedAppointmentsShowVehicleRegistration?: boolean;
  /** When true, show the primary attendee / technician identity in list / summary views. */
  linkedAppointmentsShowAssignee?: boolean;
  /** When true, show avatar / initials beside the primary attendee / technician when supported. */
  linkedAppointmentsShowAssigneeAvatar?: boolean;
  /** When true, appointment types with visible_to_customer=false are excluded from job cards. Default: false (show all). */
  linkedAppointmentsRespectVisibilityFilter?: boolean;
  /** Controls the aspect ratio of image blocks — photo-grid cells on operations
   *  cards and image summary cards such as the candidate CV photo.
   *  'square' (default) crops to a compact landscape box; '16:9' uses a taller
   *  9:16 portrait ratio — shows more of a portrait photo with less cropping. */
  photoAspectRatio?: 'square' | '16:9';
  // Table styles (finance line-item grids and any other table-based sections)
  tableBorderColor?: string;
  tableHeaderBgColor?: string;
  /**
   * When true, PDFs for this template may include the PayFast / online payment notice when the
   * organisation has PayFast configured. When false, that notice is never shown for this template.
   * When omitted: customer statements follow the organisation "link in invoices" setting; invoices do not
   * show the notice unless this is set true.
   */
  showPayfastOnPdf?: boolean;
}

/** Layout settings for a template */
export interface UnifiedTemplateLayout {
  orientation: 'portrait' | 'landscape';
  paperSize: 'a4' | 'letter' | 'legal';
  logoPosition: 'left' | 'center' | 'right';
  companyNameInHeader: boolean;
  documentTitleInHeader: boolean;
  subtitleTemplate: string;
  // Finance-specific layout
  showWatermark?: boolean;
  watermarkText?: string;
  showQRCode?: boolean;
  // Operations-specific layout
  continuousContent?: boolean;
}

/** Table column definition (for line_items_table, data_table) */
export interface TemplateTableColumn {
  key: string;
  label: string;
  width?: string;
  show: boolean;
  align?: 'left' | 'center' | 'right';
  format?: 'currency' | 'percent' | 'number' | 'date' | 'text';
}

/** Email settings embedded in a template */
export interface TemplateEmailSettings {
  subject: string;
  bodyTemplate: string;
  includeAttachment: boolean;
  attachmentFilename: string;
  emailTemplateCode?: string;    // Links to connect_email_templates.template_code
}

/** The complete template — stored in work_item_document_templates */
export interface UnifiedTemplate {
  id?: number;
  client_id?: number | null;
  name: string;
  description: string;
  document_type: DocumentType;
  document_category: DocumentCategory;
  is_active: boolean;
  is_default: boolean;
  /** True for system catalog templates (client_id = NULL). These cannot be edited directly;
   *  clients "enable" them to create a customisable copy under their own client_id. */
  is_system?: boolean;
  styles: UnifiedTemplateStyles;
  layout: UnifiedTemplateLayout;
  sections: UnifiedTemplateSection[];
  table_columns?: TemplateTableColumn[];
  custom_content?: Record<string, string>;
  email_settings?: TemplateEmailSettings;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// MERGE TAGS
// ============================================================================

/** A merge tag definition */
export interface MergeTag {
  tag: string;        // e.g., "{{ company_name }}"
  label: string;      // e.g., "Company Name"
  category: string;   // e.g., "Company", "Document", "Contact"
  example?: string;   // e.g., "Acme Corp"
}

// ============================================================================
// DEFAULT STYLES
// ============================================================================

export const DEFAULT_UNIFIED_STYLES: UnifiedTemplateStyles = {
  primaryColor: '#1a365d',
  accentColor: '#1a365d',
  textColor: '#1f2937',
  backgroundColor: '#ffffff',
  headerBgColor: '#1a365d',
  headerTextColor: '#ffffff',
  fontFamily: 'helvetica',
  fontSize: 9,
  lineHeight: 1.4,
  margins: { top: 10, right: 12, bottom: 10, left: 12 },
  headerHeight: 38,
  showPageNumbers: true,
  showGeneratedDate: true,
  linkedAppointmentsShowStatus: true,
  linkedAppointmentsDisplayMode: 'full',
  linkedAppointmentsTimeDisplayMode: 'duration',
  linkedAppointmentsReferenceLabel: 'attendance_card',
  linkedAppointmentsShowGpsProof: false,
  linkedAppointmentsShowVehicleRegistration: false,
  linkedAppointmentsShowAssignee: false,
  linkedAppointmentsShowAssigneeAvatar: false,
  linkedAppointmentsRespectVisibilityFilter: false,
  photoAspectRatio: 'square',
  tableBorderColor: '#d1d5db',
  tableHeaderBgColor: '#f3f4f6',
};

export const DEFAULT_UNIFIED_LAYOUT: UnifiedTemplateLayout = {
  orientation: 'portrait',
  paperSize: 'a4',
  logoPosition: 'left',
  companyNameInHeader: true,
  continuousContent: false,
  documentTitleInHeader: true,
  subtitleTemplate: '',
};

// ============================================================================
// LINE ITEM TABLE COLUMN PRESETS
// ============================================================================

/** Default columns for finance document line items */
export const DEFAULT_LINE_ITEM_COLUMNS: TemplateTableColumn[] = [
  { key: 'description', label: 'Description', width: '36%', show: true, align: 'left', format: 'text' },
  { key: 'quantity', label: 'Qty', width: '8%', show: true, align: 'center', format: 'number' },
  { key: 'unit', label: 'UoM', width: '8%', show: true, align: 'center', format: 'text' },
  { key: 'unitPrice', label: 'Price', width: '14%', show: true, align: 'right', format: 'currency' },
  { key: 'discount', label: 'Disc %', width: '8%', show: true, align: 'right', format: 'percent' },
  { key: 'tax', label: 'VAT', width: '12%', show: true, align: 'right', format: 'percent' },
  { key: 'total', label: 'Total', width: '14%', show: true, align: 'right', format: 'currency' },
];

/** Default columns for delivery notes (no prices) */
export const DELIVERY_NOTE_COLUMNS: TemplateTableColumn[] = [
  { key: 'description', label: 'Description', width: '55%', show: true, align: 'left', format: 'text' },
  { key: 'quantity', label: 'Qty', width: '15%', show: true, align: 'center', format: 'number' },
  { key: 'unit', label: 'Unit', width: '15%', show: true, align: 'center', format: 'text' },
  { key: 'received', label: 'Received', width: '15%', show: true, align: 'center', format: 'number' },
];

/** Default columns for warehouse movement documents (no prices/tax) */
export const WAREHOUSE_MOVEMENT_COLUMNS: TemplateTableColumn[] = [
  { key: 'description', label: 'Description', width: '60%', show: true, align: 'left', format: 'text' },
  { key: 'quantity', label: 'Qty', width: '18%', show: true, align: 'center', format: 'number' },
  { key: 'unit', label: 'Unit', width: '22%', show: true, align: 'center', format: 'text' },
];
